import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box, VStack, HStack, Button, Input, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, useToast, Spinner, Text, Flex, Heading
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon, DownloadIcon, AttachmentIcon } from '@chakra-ui/icons';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const AscFacilitiesConfig = () => {
  const [names, setNames] = useState([]);
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const fileInputRef = useRef(null);

  const REFERENCE_KEY = 'asc_facility_names';

  const fetchNames = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const response = await callApi(`/api/v1/reference-data/${REFERENCE_KEY}`, accessToken);
      if (!response.ok) throw new Error('Failed to fetch ASC facilities');
      const result = await response.json();
      setNames(result.data?.names || []);
    } catch (error) {
      toast({ title: 'Error fetching data', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNames();
  }, [accessToken]);

  const saveNames = async (updatedNames) => {
    setIsSaving(true);
    try {
      const response = await callApi(`/api/v1/reference-data/${REFERENCE_KEY}`, accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ data: { names: updatedNames } })
      });
      if (!response.ok) throw new Error('Failed to save ASC facilities');
      setNames(updatedNames);
      toast({ title: 'Saved successfully', status: 'success', duration: 2000 });
    } catch (error) {
      toast({ title: 'Error saving data', description: error.message, status: 'error', duration: 3000 });
      // Revert on failure by re-fetching
      fetchNames();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (names.includes(trimmed)) {
      toast({ title: 'Name already exists', status: 'warning', duration: 2000 });
      return;
    }
    const updatedNames = [...names, trimmed];
    setNewName('');
    saveNames(updatedNames);
  };

  const handleRemove = (nameToRemove) => {
    const updatedNames = names.filter(n => n !== nameToRemove);
    saveNames(updatedNames);
  };

  const handleDownloadCSV = () => {
    const csvContent = names.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'asc_facilities.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUploadCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);
      // Remove duplicates
      const uniqueNames = [...new Set(lines)];
      saveNames(uniqueNames);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  if (isLoading) return <Spinner size="xl" />;

  return (
    <Box bg="white" p={6} borderRadius="lg" shadow="sm" borderWidth="1px">
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="md" color="blue.700">ASC Facilities</Heading>
          <Text fontSize="sm" color="gray.600">
            Manage the list of Ambulatory Surgery Center names used to automatically assign POS 24.
          </Text>
        </VStack>
        <HStack>
          <Button leftIcon={<DownloadIcon />} variant="outline" onClick={handleDownloadCSV} isDisabled={names.length === 0}>
            Download CSV
          </Button>
          <Button leftIcon={<AttachmentIcon />} colorScheme="blue" variant="outline" onClick={() => fileInputRef.current?.click()}>
            Upload CSV
          </Button>
          <input
            type="file"
            accept=".csv,.txt"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleUploadCSV}
          />
        </HStack>
      </Flex>

      <HStack mb={6}>
        <Input 
          placeholder="Enter new ASC facility name..." 
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          isDisabled={isSaving}
        />
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleAdd} isLoading={isSaving}>
          Add
        </Button>
      </HStack>

      <Box overflowY="auto" maxHeight="500px" borderWidth="1px" borderRadius="md">
        <Table variant="simple" size="sm">
          <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
            <Tr>
              <Th>Facility Name</Th>
              <Th width="100px" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {names.length === 0 ? (
              <Tr>
                <Td colSpan={2} textAlign="center" py={4} color="gray.500">No facilities configured.</Td>
              </Tr>
            ) : (
              names.map((name, idx) => (
                <Tr key={idx}>
                  <Td>{name}</Td>
                  <Td textAlign="right">
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleRemove(name)}
                      isDisabled={isSaving}
                      aria-label="Remove facility"
                    />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default AscFacilitiesConfig;